import type {
  HubCameraConfigStatus,
  HubCameraExampleStream,
  HubDatapoolExampleStream,
  HubOneOfExampleStreamConfig,
  HubSampleFiltersUnionPart,
} from '@robovision/hub'
import { i18n } from '~i18n'
import { ConfiguredCamera } from './ConfiguredCamera'
import { ConfiguredCameraData } from './ConfiguredCameraData'
import {
  hubTriggerModeToTriggerMode,
  TriggerMode,
  triggerModeToHubTriggerMode,
} from './TriggerMode'


export enum CameraConfigurationKindIds {
  Camera = 'CAMERA',
  Datapool = 'DATAPOOL',
}

export interface CameraConfigurationKindParams {
  kind: CameraConfigurationKindIds
}

export interface CameraConfigurationCameraKindParams extends CameraConfigurationKindParams {
  cameras: ConfiguredCamera[]
  triggerMode: TriggerMode
  valid: boolean
}

export interface CameraConfigurationDatapoolKindParams extends CameraConfigurationKindParams {
  datapoolId?: string
  sampleFilters?: HubSampleFiltersUnionPart[]
  simulatedFps: number
}

export abstract class CameraConfigurationKind {
  public kind: CameraConfigurationKindIds

  constructor (params: CameraConfigurationKindParams) {
    this.kind = params.kind
  }

  public abstract toExampleStream (): HubOneOfExampleStreamConfig

  public static fromResponse (data: HubOneOfExampleStreamConfig): CameraConfigurationKind {
    switch (data.kind) {
      case CameraConfigurationKindIds.Camera:
        return CameraConfigurationCameraKind.fromResponse(data as HubCameraExampleStream)
      case CameraConfigurationKindIds.Datapool:
        return CameraConfigurationDatapoolKind.fromResponse(data as HubDatapoolExampleStream)
      default:
        throw new Error('Invalid camera configuration kind')
    }
  }

  /**
   * Should return the list of cameras linked
   * to the configuration (if any)
   */
  public abstract getLinkedCameras (): readonly ConfiguredCamera[]
}

export class CameraConfigurationCameraKind extends CameraConfigurationKind {
  public cameras: ConfiguredCamera[]
  public triggerMode: TriggerMode
  public valid: boolean

  constructor (params: CameraConfigurationCameraKindParams) {
    super(params)
    this.cameras = params.cameras
    this.triggerMode = params.triggerMode
    this.valid = params.valid
  }

  public get triggerModeLabel (): string {
    switch (this.triggerMode) {
      case TriggerMode.ApiSignal:
        return i18n.t('cameras.triggerMode.apiSignal')
      case TriggerMode.CameraConfig:
        return i18n.t('cameras.triggerMode.cameraConfig')
      default:
        return this.triggerMode.toString()
    }
  }

  public static override fromResponse (data: HubCameraExampleStream): CameraConfigurationCameraKind {
    return new CameraConfigurationCameraKind({
      cameras: data.cameras.map(ConfiguredCamera.fromResponse),
      kind: CameraConfigurationKindIds.Camera,
      triggerMode: hubTriggerModeToTriggerMode(data.triggerMode),
      valid: data.valid,
    })
  }

  public override getLinkedCameras (): readonly ConfiguredCamera[] {
    return this.cameras
  }

  public toData (): ConfiguredCameraData[] {
    return this.cameras.map(camera => camera.toData())
  }

  public toExampleStream (): HubCameraExampleStream {
    return {
      cameras: this.cameras.map(c => ({
        cameraCreatedAt: c.createdAt.toISOString(),
        cameraId: c.id,
        cameraName: c.name,
        cameraSerialNumber: c.serialNumber,
        cameraStatus: c.status,
        cameraType: c.cameraType,
        configName: c.configName,
        configStatus: c.configStatus as HubCameraConfigStatus,
        configStatusMessage: c.configStatusMessage,
        viewName: c.viewName,
        viewPath: c.viewPath,
      })),
      kind: this.kind as 'CAMERA',
      triggerMode: triggerModeToHubTriggerMode(this.triggerMode),
      valid: this.valid,
    }
  }
}

export class CameraConfigurationDatapoolKind extends CameraConfigurationKind {
  public datapoolId?: string
  public sampleFilters?: HubSampleFiltersUnionPart[]
  public simulatedFps: number

  constructor (params: CameraConfigurationDatapoolKindParams) {
    super(params)
    this.datapoolId = params.datapoolId
    this.sampleFilters = params.sampleFilters
    this.simulatedFps = params.simulatedFps
  }

  public static override fromResponse (data: HubDatapoolExampleStream): CameraConfigurationDatapoolKind {
    return new CameraConfigurationDatapoolKind({
      datapoolId: data.datapoolId,
      kind: CameraConfigurationKindIds.Datapool,
      sampleFilters: data.sampleFilters,
      simulatedFps: data.simulatedFps,
    })
  }

  public override getLinkedCameras (): ConfiguredCamera[] {
    return []
  }

  public toExampleStream (): HubDatapoolExampleStream {
    return {
      datapoolId: this.datapoolId,
      kind: this.kind as 'DATAPOOL',
      sampleFilters: this.sampleFilters,
      simulatedFps: this.simulatedFps,
    }
  }
}
